'use client';

import React from 'react';
import type { Locale } from '@/lib/i18n';

type LocaleContextValue = {
  locale: Locale;
  geoCountryCode: string | null;
};

export const LocaleContext = React.createContext<LocaleContextValue | null>(null);

export function LocaleProvider({
  locale,
  geoCountryCode = null,
  children,
}: {
  locale: Locale;
  geoCountryCode?: string | null;
  children: React.ReactNode;
}) {
  React.useEffect(() => {
    const oneYear = 60 * 60 * 24 * 365;
    document.cookie = `NEXT_LOCALE=${locale}; Path=/; Max-Age=${oneYear}; SameSite=Lax`;
  }, [locale]);

  return (
    <LocaleContext.Provider value={{ locale, geoCountryCode }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale(): Locale {
  const ctx = React.useContext(LocaleContext);
  if (!ctx) {
    throw new Error('useLocale must be used within LocaleProvider');
  }
  return ctx.locale;
}

export function useGeoCountryCode(): string | null {
  const ctx = React.useContext(LocaleContext);
  return ctx?.geoCountryCode ?? null;
}
